import { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { toast } from 'react-toastify';

import { Modal } from '../../../../components/Modal/Modal';
import { useBulkSendBol } from '../../api';
import { orderIdOf } from '../grain';
import { summarizeBulk } from '../summarize';
import { canSendBol, planSendBol } from './sendBolPlan';
import type { BulkAction, BulkActionBarContext } from '../types';

// Send each eligible selected load's BOL to that load's default recipient(s).
// Recipients are per-load (each load's own carrier), so there's no shared editor.
function SendBolDialog({ ctx, onClose }: { ctx: BulkActionBarContext; onClose: () => void }) {
  const { rows, count } = ctx;
  const bulkSend = useBulkSendBol();
  const isApplying = bulkSend.isPending;

  const plan = useMemo(() => planSendBol(rows), [rows]);
  const eligibleCount = plan.eligible.length;

  const apply = async () => {
    if (eligibleCount === 0) return;

    try {
      const res = await bulkSend.mutateAsync({
        items: plan.eligible.map(({ row, emails }) => ({
          orderId: orderIdOf(row) ?? '',
          itemId: row.id,
          loadNumber: row.loadNumber,
          emails,
        })),
      });

      const summary = summarizeBulk(res.succeeded, res.failed, eligibleCount, 'sent');
      if (summary.tone === 'success') toast.success(summary.message);
      else toast.warn(summary.message);
      if (summary.clearSelection) ctx.onClearSelection();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to send the BOLs.');
    }
  };

  return (
    <Modal
      isOpen
      onClose={isApplying ? () => {} : onClose}
      title={`Send ${eligibleCount} BOL${eligibleCount === 1 ? '' : 's'}`}
      size="sm"
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={isApplying}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={apply}
            disabled={isApplying || eligibleCount === 0}
          >
            {isApplying ? 'Sending…' : `Send ${eligibleCount}`}
          </button>
        </>
      }
    >
      {eligibleCount === 0 ? (
        <p>
          None of the {count} selected load{count === 1 ? '' : 's'} can have a BOL sent. A load
          needs a carrier, a pickup date, and a carrier email on file.
        </p>
      ) : (
        <p>
          The BOL for <strong>{eligibleCount}</strong> of {count} selected load
          {count === 1 ? '' : 's'} will be emailed to each load&apos;s default recipient(s).
        </p>
      )}

      {plan.skipped.length > 0 && (
        <ul className="bulk-confirm__list">
          {plan.skipped.map((bucket) => (
            <li key={bucket.reason}>
              {bucket.rows.length} skipped ({bucket.reason}).
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function SendBolTrigger({ ctx }: { ctx: BulkActionBarContext }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
        <FileText size={14} />
        Send BOL
      </button>

      {open && <SendBolDialog ctx={ctx} onClose={() => setOpen(false)} />}
    </>
  );
}

export const SendBolAction: BulkAction = {
  id: 'send-bol',
  isAvailable: (ctx) => ctx.rows.some(canSendBol),
  Component: SendBolTrigger,
};
